// src/cache.js - TTL-based caching utility for localStorage

const CACHE_PREFIX = 'mars_cache_';

// Cache TTL configurations (in milliseconds)
const CACHE_TTL = {
    home: 5 * 60 * 1000,        // 5 minutes for home data
    games: 10 * 60 * 1000,      // 10 minutes for games list
    categories: 60 * 60 * 1000, // 1 hour for categories
    leaderboard: 30 * 1000,     // 30 seconds for leaderboard
    config: 24 * 60 * 60 * 1000 // 24 hours for config
};

/**
 * Get cached data if not expired
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null if expired/missing
 */
export function getCache(key) {
    try {
        const cached = localStorage.getItem(CACHE_PREFIX + key);
        if (!cached) return null;

        const { data, timestamp, ttl } = JSON.parse(cached);
        const now = Date.now();

        if (now - timestamp > ttl) {
            // Cache expired, remove it
            localStorage.removeItem(CACHE_PREFIX + key);
            return null;
        }

        return data;
    } catch (e) {
        console.warn('Cache read error:', e);
        return null;
    }
}

/**
 * Set cached data with TTL
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {string} ttlKey - Key to lookup TTL from CACHE_TTL config
 */
export function setCache(key, data, ttlKey = 'home') {
    try {
        const ttl = CACHE_TTL[ttlKey] || CACHE_TTL.home;
        const cacheEntry = {
            data,
            timestamp: Date.now(),
            ttl
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    } catch (e) {
        console.warn('Cache write error:', e);
        // If quota exceeded, clear old cache entries
        if (e.name === 'QuotaExceededError') {
            clearExpiredCache();
        }
    }
}

/**
 * Clear specific cache key
 * @param {string} key - Cache key to clear
 */
export function clearCache(key) {
    localStorage.removeItem(CACHE_PREFIX + key);
}

/**
 * Clear all expired cache entries
 */
export function clearExpiredCache() {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            try {
                const cached = localStorage.getItem(key);
                if (cached) {
                    const { timestamp, ttl } = JSON.parse(cached);
                    if (now - timestamp > ttl) {
                        keysToRemove.push(key);
                    }
                }
            } catch (e) {
                keysToRemove.push(key);
            }
        }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
}

/**
 * Clear all cache entries
 */
export function clearAllCache() {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_PREFIX)) {
            keysToRemove.push(key);
        }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
}

export default {
    get: getCache,
    set: setCache,
    clear: clearCache,
    clearExpired: clearExpiredCache,
    clearAll: clearAllCache,
    TTL: CACHE_TTL
};
